import { useCallback, useRef } from "react"
import { useDownloadQuotationResultCsv } from "hooks/api/quotation/quotations.hook"
import { useMessage } from "hooks/message.hook"
import { RESULT_CSV_MESSAGES } from "helpers/resultCsv.constants"
import type { MutableRefObject } from "react"

type Props = {
	// Read at click time rather than captured: see the identity note below.
	quotationIdRef: MutableRefObject<number | undefined>
	// Parts of the download name. Passed as plain values and mirrored into a ref below, so the menu
	// picks up whatever has loaded by the time it is clicked. Both are `undefined` only while their
	// query is unsettled — never because a quotation lacks a name (the API substitutes "Untitled").
	quotationName?: string
	drawingName?: string | null
}

// The editor's entry point for the on-demand result CSV (REQ-420). Exists as its own hook for one
// reason worth stating: the burger menu is rendered ONCE into the Apryse shadow DOM by a
// `customElement.render()` callback, so whatever function it receives is captured for the lifetime of
// the viewer. This returns a callback with a permanently stable identity that reads the current
// quotation id from a ref, which is what makes that capture safe.
export const useResultCsvDownload = ({ quotationIdRef, quotationName, drawingName }: Props) => {
	const { downloadResultCsv: downloadCsv } = useDownloadQuotationResultCsv()
	const { error: errorMessage } = useMessage()

	// `namesRef` is load-bearing: these values change as queries settle, and the dependency array
	// cannot see them without breaking the identity guarantee above.
	const namesRef = useRef({ quotationName, drawingName })
	namesRef.current = { quotationName, drawingName }
	// These two are already `useCallback`-stable today (their deps chain back to a `useMemo([])`), so
	// the refs are insurance rather than a fix: they keep the guarantee local, so adding a dependency
	// inside `useMessage` or the download hook cannot silently hand the header a stale closure.
	const downloadCsvRef = useRef(downloadCsv)
	downloadCsvRef.current = downloadCsv
	const errorMessageRef = useRef(errorMessage)
	errorMessageRef.current = errorMessage

	const downloadResultCsv = useCallback(async () => {
		const quotationId = quotationIdRef.current
		const { quotationName, drawingName } = namesRef.current
		if (quotationId == null || !quotationName) {
			// Shouldn't happen — the editor is only reachable with a quotation in the URL, and opening it
			// prefetches the quotation via `ensureQueryData` — but a menu click must never be a silent
			// no-op, and the name is not something to invent.
			errorMessageRef.current(RESULT_CSV_MESSAGES.failed)
			return
		}
		await downloadCsvRef.current({ quotationId, quotationName, drawingName })
	}, [quotationIdRef])

	return { downloadResultCsv }
}
